// src/components/pages/Contact.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../../context/CartContext';
import { jcb } from '../../api/apiClient';

// ============================================================
// Opciones de interés agrupadas
// ============================================================
const INTEREST_GROUPS = [
  {
    label: 'Maquinaria',
    options: [
      { value: 'maquinaria-brazo-articulado', label: 'Brazos Articulados' },
      { value: 'maquinaria-cargador-frontal', label: 'Cargadores Frontales' },
      { value: 'maquinaria-compactadora', label: 'Compactadoras' },
      { value: 'maquinaria-excavadora', label: 'Excavadoras' },
      { value: 'maquinaria-manipulador-telescopico', label: 'Manipuladores Telescópicos' },
      { value: 'maquinaria-minicargador', label: 'Minicargadores' },
      { value: 'maquinaria-miniexcavadora', label: 'Miniexcavadoras' },
      { value: 'maquinaria-montacargas', label: 'Montacargas' },
      { value: 'maquinaria-plataforma-altura', label: 'Plataformas de Altura' },
      { value: 'maquinaria-retroexcavadora', label: 'Retroexcavadoras' },
      { value: 'maquinaria-teletruk', label: 'Teletruk' },
    ],
  },
  {
    label: 'Aditamentos y Refacciones',
    options: [
      { value: 'aditamento', label: 'Aditamento' },
      { value: 'refaccion', label: 'Refacción' },
    ],
  },
  {
    label: 'Servicio y Postventa',
    options: [
      { value: 'servicio-tecnico', label: 'Servicio Técnico / Mantenimiento' },
      { value: 'servicio-refacciones', label: 'Cotización de Refacciones' },
    ],
  },
  {
    label: 'Negocios y otros',
    options: [
      { value: 'proveedor-servicios', label: 'Ofrecer servicios / Proveedor' },
      { value: 'marketing-alianzas', label: 'Marketing / Alianzas comerciales' },
      { value: 'otro', label: 'Otro requerimiento' },
    ],
  },
];

const Contact = () => {
  const { cartItems, clearCart } = useCart();

  const [branches, setBranches] = useState({});
  const [loadingBranches, setLoadingBranches] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    tel: '',
    branch: '',
    interest: '',
    notes: '',
  });

  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ============================================================
  // Cargar sucursales desde el backend
  // ============================================================
  useEffect(() => {
    let isMounted = true;
    const loadBranches = async () => {
      try {
        const { data } = await jcb.get('/branches');
        if (!isMounted) return;
        setBranches(data.branches || {});
      } catch (err) {
        console.error('Error cargando sucursales:', err);
      } finally {
        if (isMounted) setLoadingBranches(false);
      }
    };
    loadBranches();
    return () => { isMounted = false; };
  }, []);

  const branchList = Object.values(branches);
  const selectedBranch = formData.branch ? branches[formData.branch] : null;

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  // ============================================================
  // Separar items del carrito por tipo
  // ============================================================
  const machineRequests = cartItems.filter(i => i.itemType === 'machineRequest');
  const machineryItems = cartItems.filter(i => i.itemType === 'machinery');
  const spareParts = cartItems.filter(i => i.itemType === 'sparePart');

  // ============================================================
  // Construir payload para el backend
  // ============================================================
  const buildCartItemsPayload = () => {
    const payload = [];

    machineryItems.forEach(item => {
      payload.push({
        type: 'machinery',
        item: {
          id: item.id,
          name: item.name,
          machineryType: item.machineryType || null,
          model: item.model || null,
          price: item.price || 0,
          quantity: item.quantity,
        },
      });
    });

    spareParts.forEach(item => {
      payload.push({
        type: 'sparePart',
        item: {
          id: item.id,
          name: item.name,
          partNumber: item.partNumber || null,
          price: item.price || 0,
          quantity: item.quantity,
          context: item.context || {},
          contextKey: item.contextKey,
        },
      });
    });

    machineRequests.forEach(req => {
      payload.push({
        type: 'machineRequest',
        item: {
          id: req.id,
          machineryType: req.machineryType,
          model: req.model,
          year: req.year,
          area: req.area,
        },
      });
    });

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.branch) {
      setError('Debes seleccionar una sucursal.');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.tel.trim() || null,
        branch: formData.branch,
        interest: formData.interest || null,
        message: formData.notes.trim() || null,
        source: 'contact-page',
        cart_items: buildCartItemsPayload(),
      };

      await jcb.post('/contact', payload);

      setSuccess(true);
      setFormData({ name: '', email: '', tel: '', branch: '', interest: '', notes: '' });
      clearCart();
      setTimeout(() => setSuccess(false), 6000);
    } catch (err) {
      console.error('Error enviando mensaje:', err);
      const backendMessage = err.response?.data?.message;
      const validationErrors = err.response?.data?.errors;
      let finalError = 'Error al enviar el mensaje. Por favor, intenta de nuevo.';

      if (backendMessage) finalError = backendMessage;
      if (validationErrors) {
        const first = Object.values(validationErrors)[0];
        if (Array.isArray(first) && first[0]) finalError = first[0];
      }
      setError(finalError);
    } finally {
      setLoading(false);
    }
  };

  const hasCartItems = cartItems.length > 0;

  return (
    <div className="max-w-[1680px] mx-auto px-6 sm:px-12 py-24">
      <Helmet>
        <title>Contacto | JCB Azteca</title>
        <meta name="description" content="Cotiza maquinaria, refacciones o servicio técnico JCB. Sucursales en Villahermosa, Mérida, Veracruz y Puerto Morelos." />
        <link rel="canonical" href="https://azteca-site.org/contact" />
        <meta property="og:title" content="Contacto | JCB Azteca" />
        <meta property="og:description" content="Cotiza maquinaria, refacciones o servicio técnico JCB." />
        <meta property="og:url" content="https://azteca-site.org/contact" />
        <meta property="og:image" content="https://azteca-site.org/og/contacto-jcb-azteca.jpg" />
        <meta name="twitter:title" content="Contacto | JCB Azteca" />
        <meta name="twitter:description" content="Cotiza maquinaria, refacciones o servicio técnico JCB." />
        <meta name="twitter:image" content="https://azteca-site.org/og/contacto-jcb-azteca.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contacto | JCB Azteca",
            "url": "https://azteca-site.org/contact",
            "description": "Cotiza maquinaria, refacciones o servicio técnico JCB en el sureste de México.",
            "mainEntity": {
              "@type": "Organization",
              "name": "JCB Azteca",
              "telephone": "+52-993-478-4100",
              "url": "https://azteca-site.org/"
            }
          })}
        </script>
      </Helmet>

      <nav className="text-sm mb-8">
        <Link to="/" className="text-text-muted hover:text-dark-primary">Inicio</Link>
        <span className="mx-2 text-text-muted">/</span>
        <span className="text-dark-primary font-medium">Contacto</span>
      </nav>

      <h1 className="font-display text-4xl sm:text-5xl font-semibold uppercase tracking-tight-headline text-dark-primary mb-8">
        {hasCartItems ? 'Solicitar Cotización' : 'Contáctanos'}
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* ============================================================
            COLUMNA IZQUIERDA
            ============================================================ */}
        <div>
          {hasCartItems ? (
            <div>
              <p className="text-lg text-text-muted mb-6">
                Estás a punto de solicitar una cotización para los siguientes items:
              </p>

              <div className="space-y-6">
                {/* 1) MAQUINARIA */}
                {machineryItems.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-display text-[10px] uppercase tracking-luxury text-text-muted font-semibold">
                      Maquinaria
                    </p>
                    <div className="bg-white border border-border-subtle divide-y divide-border-subtle">
                      {machineryItems.map((item) => (
                        <div key={item.id} className="p-4">
                          <p className="font-display text-sm font-semibold text-dark-primary">
                            {item.machineryType || item.name}
                          </p>
                          {item.model && (
                            <p className="font-display text-xs text-dark-primary/70 mt-0.5">
                              {item.model}
                            </p>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs font-display font-medium text-jcb-yellow">
                              {item.price === 0 || item.price == null
                                ? 'A cotizar'
                                : `$${Number(item.price).toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })} USD`}
                            </p>
                            <span className="text-xs text-text-muted font-display">
                              x{item.quantity}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 2) REFACCIONES */}
                {spareParts.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-display text-[10px] uppercase tracking-luxury text-text-muted font-semibold">
                      Refacciones
                    </p>
                    <div className="bg-white border border-border-subtle divide-y divide-border-subtle">
                      {spareParts.map((item) => (
                        <div key={`${item.id}-${item.contextKey}`} className="p-4">
                          <p className="font-display text-sm font-medium text-dark-primary">
                            {item.name}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-xs font-display font-medium text-jcb-yellow">
                              {item.price === 0 || item.price == null
                                ? 'A cotizar'
                                : `$${Number(item.price).toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })} USD`}
                            </p>
                            <span className="text-xs text-text-muted font-display">
                              x{item.quantity}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 3) SOLICITUDES DE REFACCIONES */}
                {machineRequests.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-display text-[10px] uppercase tracking-luxury text-text-muted font-semibold">
                      Solicitudes de Refacciones
                    </p>
                    <div className="bg-white border border-border-subtle divide-y divide-border-subtle">
                      {machineRequests.map((req) => (
                        <div key={req.id} className="p-4">
                          <p className="font-display text-sm font-semibold text-dark-primary">
                            {req.machineryType}
                          </p>
                          <p className="font-display text-xs text-dark-primary/70 mt-0.5">
                            {req.model} {req.year}
                          </p>
                          <p className="text-xs text-text-muted mt-1">
                            Área: {req.area}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3">
                <Link
                  to="/cart"
                  className="font-display text-xs uppercase tracking-luxury font-medium border border-border-medium text-text-muted hover:text-dark-primary hover:border-dark-primary px-4 py-2 transition-all"
                >
                  Editar carrito
                </Link>
                <button
                  type="button"
                  onClick={clearCart}
                  className="font-display text-xs uppercase tracking-luxury font-medium border border-border-medium text-red-500 hover:text-red-700 hover:border-red-300 px-4 py-2 transition-all"
                >
                  Vaciar
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-lg text-text-muted mb-6">
                Estamos aquí para ayudarte con tus necesidades de maquinaria y refacciones.
              </p>

              {selectedBranch ? (
                <div className="bg-surface-subtle p-6 border border-border-subtle">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-display text-[10px] uppercase tracking-luxury bg-dark-primary text-white px-3 py-1 font-medium">
                      {selectedBranch.role}
                    </span>
                    <span className="font-display text-xs uppercase tracking-luxury text-text-muted font-medium">
                      {selectedBranch.state}
                    </span>
                  </div>
                  <h3 className="font-display text-xl font-semibold text-dark-primary mb-4">
                    {selectedBranch.name}
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <h4 className="font-display text-xs uppercase tracking-luxury text-text-subtle mb-1">
                        Dirección
                      </h4>
                      <p className="text-dark-primary">{selectedBranch.address}</p>
                    </div>
                    <div>
                      <h4 className="font-display text-xs uppercase tracking-luxury text-text-subtle mb-1">
                        Teléfono
                      </h4>
                      <p className="font-medium text-dark-primary">{selectedBranch.phone}</p>
                    </div>
                    <div>
                      <h4 className="font-display text-xs uppercase tracking-luxury text-text-subtle mb-1">
                        Email
                      </h4>
                      <p className="font-medium text-dark-primary">{selectedBranch.email}</p>
                    </div>
                    <div>
                      <h4 className="font-display text-xs uppercase tracking-luxury text-text-subtle mb-1">
                        Horario
                      </h4>
                      <p className="text-text-muted">{selectedBranch.hours}</p>
                    </div>
                  </div>
                  {selectedBranch.maplink && (
                    <div className="mt-6 pt-4 border-t border-border-medium">
                      <a
                        href={selectedBranch.maplink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 font-display text-xs uppercase tracking-luxury font-semibold text-dark-primary hover:text-jcb-yellow transition-colors group"
                      >
                        <span>Cómo llegar</span>
                        <span className="material-symbols-outlined text-sm transition-transform group-hover:translate-x-1">
                          north_east
                        </span>
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-surface-subtle p-6 border border-border-subtle">
                  <p className="text-sm text-text-muted italic">
                    Selecciona una sucursal para ver su información de contacto.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* ============================================================
            COLUMNA DERECHA — FORMULARIO
            ============================================================ */}
        <div className="bg-surface-subtle p-8 border border-border-subtle">
          {success && (
            <div className="p-4 bg-white border-l-2 border-jcb-yellow text-xs text-dark-primary mb-4">
              {hasCartItems
                ? '¡Cotización enviada correctamente! Te contactaremos pronto.'
                : '¡Mensaje enviado correctamente! Te contactaremos pronto.'}
            </div>
          )}
          {error && (
            <div className="p-4 bg-red-50 border-l-2 border-red-500 text-xs text-red-700 mb-4">
              {error}
            </div>
          )}

          <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
            <div>
              <label className="font-display text-xs uppercase tracking-luxury text-dark-primary font-medium block mb-2" htmlFor="name">
                Nombre completo *
              </label>
              <input
                className="w-full bg-white border border-border-medium px-4 py-3.5 text-sm text-dark-primary placeholder-text-subtle focus:outline-none focus:border-dark-primary transition-all rounded-none"
                id="name"
                placeholder="Ing. Alejandro Reyes"
                required
                type="text"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="font-display text-xs uppercase tracking-luxury text-dark-primary font-medium block mb-2" htmlFor="email">
                Email *
              </label>
              <input
                className="w-full bg-white border border-border-medium px-4 py-3.5 text-sm text-dark-primary placeholder-text-subtle focus:outline-none focus:border-dark-primary transition-all rounded-none"
                id="email"
                placeholder="tu@empresa.com"
                required
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="font-display text-xs uppercase tracking-luxury text-dark-primary font-medium block mb-2" htmlFor="tel">
                Teléfono *
              </label>
              <input
                className="w-full bg-white border border-border-medium px-4 py-3.5 text-sm text-dark-primary placeholder-text-subtle focus:outline-none focus:border-dark-primary transition-all rounded-none"
                id="tel"
                placeholder="+52 (55) 0000 0000"
                required
                type="tel"
                value={formData.tel}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="font-display text-xs uppercase tracking-luxury text-dark-primary font-medium block mb-2" htmlFor="branch">
                Sucursal *
              </label>
              <select
                className="w-full bg-white border border-border-medium px-4 py-3.5 text-sm text-dark-primary focus:outline-none focus:border-dark-primary transition-all rounded-none cursor-pointer disabled:opacity-60"
                id="branch"
                value={formData.branch}
                onChange={handleChange}
                disabled={loadingBranches}
                required
              >
                <option value="" disabled>
                  {loadingBranches ? 'Cargando sucursales...' : 'Seleccionar sucursal...'}
                </option>
                {branchList.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            {!hasCartItems && (
              <div>
                <label className="font-display text-xs uppercase tracking-luxury text-dark-primary font-medium block mb-2" htmlFor="interest">
                  ¿Qué estás buscando?
                </label>
                <select
                  className="w-full bg-white border border-border-medium px-4 py-3.5 text-sm text-dark-primary focus:outline-none focus:border-dark-primary transition-all rounded-none cursor-pointer"
                  id="interest"
                  value={formData.interest}
                  onChange={handleChange}
                >
                  <option value="">Seleccionar una opción...</option>
                  {INTEREST_GROUPS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="font-display text-xs uppercase tracking-luxury text-dark-primary font-medium block mb-2" htmlFor="notes">
                Mensaje
              </label>
              <textarea
                className="w-full bg-white border border-border-medium px-4 py-3.5 text-sm text-dark-primary placeholder-text-subtle focus:outline-none focus:border-dark-primary transition-all rounded-none resize-none"
                id="notes"
                placeholder={hasCartItems
                  ? "Agrega cualquier detalle adicional sobre tu cotización..."
                  : "Indica especificaciones de tu obra, tiempo estimado de entrega o refacciones deseadas..."}
                rows="4"
                value={formData.notes}
                onChange={handleChange}
              />
            </div>

            <div className="pt-2">
              <button
                className="w-full font-display text-xs uppercase tracking-luxury font-semibold bg-dark-primary hover:bg-black text-white py-4 px-8 transition-all flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                type="submit"
                disabled={loading || loadingBranches}
              >
                <span>{loading ? 'Enviando...' : (hasCartItems ? 'Enviar cotización' : 'Enviar solicitud')}</span>
                <span className="material-symbols-outlined text-sm text-jcb-yellow">arrow_forward</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;